/**
 * @Description 评分概要(包含预测营业额) 四维评分时使用
 */

import { FC, useMemo, useRef } from 'react';
import { Typography } from 'antd';
import { isNotEmpty, isNotEmptyAny } from '@lhb/func';
import IconFont from '@/common/components/IconFont';
import SiteMapExplainExtra from '@/common/components/business/SiteMap/SiteMapExplainExtra';
import ScoreFourOwnScore from '../ScoreFourOwnScore';
import TotalSalesAmountPredict from '../TotalSalesAmountPredict';
import { XTCScoreType } from '../../ts-config';
import styles from './index.module.less';
import cs from 'classnames';
import ScoreFourOwnScoreHasScore from '../ScoreFourOwnScore/index-has-score';
import { fullEditorCss } from '@/common/components/config-v2';

const { Text } = Typography;

const ScoreFour: FC<{
  overviewData: any;
  predictSale?: boolean;
  configSwitch: any;
  shareParams?: Object;// 分享页数据
  /** 是否是pdf */
  isPdf?: boolean;
  detail?: any;
}> = ({
  overviewData,
  predictSale = false,
  configSwitch,
  shareParams = {},
  isPdf = false,
  detail,
}) => {
  const shadowRef: any = useRef();
  const showTotalScore = !!configSwitch?.showTotalScore; // 是否显示总分
  const showBenefit = !!configSwitch?.showIndustryBenefitContent; // 是否展示有利于
  const showXtcScore = isNotEmpty(overviewData?.category1SalesAmountPredict); // 是否展示xtc评分
  const showBbgScore = isNotEmpty(overviewData?.category2SalesAmountPredict); // 是否展示bbg评分
  const showNormalScore = !showXtcScore && !showXtcScore && isNotEmpty(overviewData?.salesAmountPredict); // 是否展示原始类型
  const showBothScore = showXtcScore && showBbgScore;

  const evaluateInfoStr = useMemo(() => {
    const { marketScore, firstLevelName } = overviewData || {};
    if (marketScore > 70) {
      return `有利于${firstLevelName || ''}经营`;
    }
    if (marketScore > 50 && marketScore <= 70) {
      return `经营${firstLevelName || ''}一般`;
    }
    if (marketScore <= 50) {
      return `不利于${firstLevelName || ''}经营`;
    }
    return '-';
  }, [overviewData]);
  const showScoreDescriptionFlag = useMemo(() => {
    // 1：默认值，2：自定义富文本，3：不展示
    // 如果没有这个参数，也就是没设置过，就沿用老逻辑展示默认值。
    if (!isNotEmpty(detail.showScoreDescriptionFlag)) {
      return 1;
    } else {
      return detail.showScoreDescriptionFlag;
    }
  }, [detail.showScoreDescriptionFlag]);
  return (
    <div className={cs(styles.scoreFourDimen, 'pl-16', showBothScore ? 'pr-16' : 'pr-20')}>
      {showTotalScore && <div className={styles.totalScoreWrapper}>
        <div className={styles.scoreTitle}>评分</div>
        <div className={styles.totalScoreInfo}>
          <div className={cs(styles.totalScoreVal, 'mr-4')}>{overviewData?.marketScore}</div>
          {[1, 2].includes(showScoreDescriptionFlag) && !isPdf && <div>
            <IconFont
              iconHref='iconxq_ic_shuoming_normal'
              className='mr-2 c-666 fs-12'
              tooltipConfig={{
                placement: 'bottom',
                overlayInnerStyle: {
                  width: '380px',
                  padding: '16px',
                  boxSizing: 'border-box',
                  borderRadius: '4px',
                },
                color: '#fff',
                onOpenChange(open) {
                  // left: 737px; top: 137px; transform-origin: 50% -4px;
                  if (showScoreDescriptionFlag !== 1 && detail.scoreDescription && open) {
                    setTimeout(() => {
                      // 创建Shadow DOM根节点
                      const shadowRoot = shadowRef.current.attachShadow({ mode: 'open' });
                      const contentDiv = document.createElement('div');
                      contentDiv.innerHTML = detail.scoreDescription;
                      // 创建样式
                      const style = document.createElement('style');
                      style.textContent = fullEditorCss;
                      // 添加到Shadow DOM
                      shadowRoot.appendChild(style);
                      shadowRoot.appendChild(contentDiv);
                    }, 1);
                  }
                },
                title: showScoreDescriptionFlag === 1 ? (
                  <SiteMapExplainExtra
                    markType={overviewData?.markType}
                    isPredictSale={predictSale}
                    shareParams={shareParams}
                  />
                ) : (
                  <div ref={shadowRef}></div>
                ),
              }}
            />
          </div>}
          { showBenefit ? <div className='c-222 fs-12'>
            <Text
              style={isNotEmptyAny(overviewData.rankingText) ? { maxWidth: '150px' } : { maxWidth: '250px' }}
              ellipsis={{ tooltip: evaluateInfoStr }}
              className='c-222 fs-12'
            >
              <span>({evaluateInfoStr})</span>
            </Text>
          </div> : <></> }
        </div>
      </div>}
      {showTotalScore && <TotalSalesAmountPredict
        category1SalesAmountPredict={overviewData?.category1SalesAmountPredict}
        category2SalesAmountPredict={overviewData?.category2SalesAmountPredict}
      />}

      {/* 占位图片，总分+xtc+bbg评分都显示时空间有限需要隐藏 */}
      {!(showTotalScore && showBothScore) && <div className={cs(styles.placeholderImg, 'ml-16')}>
        <div className={styles.imgWrapper}>
          <img src={`${process.env.STATIC_RES_DOMAIN}/project-custom/locationpc/selectionmap/icon_score.png`} width='100%' height='100%'/>
        </div>
      </div>}
      {
        showBothScore ? (
          <>
            {/* xtc评分+预测营业额、bbg评分+预测营业额 */}
            {showXtcScore && <ScoreFourOwnScoreHasScore
              scoreType={XTCScoreType.XTC}
              score={overviewData?.category1MarketScore}
              timeDimension={overviewData?.timeDimension}
              salesAmountPredict
                ={overviewData?.category1SalesAmountPredict}
              showPredictIcon={!showBothScore}
              wrapperClassName={showBothScore ? 'ml-8' : 'ml-20'}
            />}
            {showBbgScore && <ScoreFourOwnScoreHasScore
              scoreType={XTCScoreType.BBG}
              score={overviewData?.category2MarketScore}
              timeDimension={overviewData?.timeDimension}
              salesAmountPredict
                ={overviewData?.category2SalesAmountPredict}
              showPredictIcon={!showBothScore}
              wrapperClassName={showBothScore ? 'ml-8' : 'ml-20'}
            />}
          </>
        ) : (
          <>
            {/* xtc评分+预测营业额、bbg评分+预测营业额 */}
            {showXtcScore && <ScoreFourOwnScore
              timeDimension={overviewData?.timeDimension}
              salesAmountPredict
                ={overviewData?.category1SalesAmountPredict}
              showPredictIcon={!showBothScore}
              salesPredict={overviewData?.category1SalesPredict}
              wrapperClassName={showBothScore ? 'ml-8' : 'ml-20'}
            />}
            {showBbgScore && <ScoreFourOwnScore
              timeDimension={overviewData?.timeDimension}
              salesAmountPredict
                ={overviewData?.category2SalesAmountPredict}
              showPredictIcon={!showBothScore}
              salesPredict={overviewData?.category2SalesPredict}
              wrapperClassName={showBothScore ? 'ml-8' : 'ml-20'}
            />}
            {showNormalScore && <ScoreFourOwnScore
              timeDimension={overviewData?.timeDimension}
              salesAmountPredict={overviewData?.salesAmountPredict}
              showPredictIcon
              salesPredict={overviewData?.salesPredict}
              wrapperClassName={'ml-20'}
            />}
          </>
        )
      }


    </div>
  );
};

export default ScoreFour;
